/**
 * What a square says about the selected suspect, and what one more tap does to it.
 *
 * There is exactly one interaction in this game: tap a square. Each tap advances that square one
 * step through the cycle below, for whoever is currently selected. No modes, no long-press, so the
 * same gesture works with a mouse, a finger or a keyboard.
 */

import type { CellIdx } from "@engine/types";

export type CellMark = "none" | "placed" | "maybe" | "no";

export interface BoardMarks {
  placements: (number | null)[];
  /** Cell key -> characters pencilled in as a maybe. */
  notes: Record<string, number[]>;
  /** Cell key -> characters ruled out of that square. */
  exclusions: Record<string, number[]>;
}

export function markFor(marks: BoardMarks, char: number, cell: CellIdx): CellMark {
  if (marks.placements[char] === cell) return "placed";
  const key = String(cell);
  if ((marks.notes[key] ?? []).includes(char)) return "maybe";
  if ((marks.exclusions[key] ?? []).includes(char)) return "no";
  return "none";
}

/**
 * The next mark in the cycle: none → placed → maybe → no → none.
 *
 * `canPlace` is false when the square already clashes with someone else's row or column. There the
 * cycle skips straight to "maybe", because offering a placement we know is illegal would light up a
 * contradiction warning the player did not ask for.
 */
export function nextMark(current: CellMark, canPlace: boolean): CellMark {
  switch (current) {
    case "none":
      return canPlace ? "placed" : "maybe";
    case "placed":
      return "maybe";
    case "maybe":
      return "no";
    case "no":
      return "none";
  }
}

const without = (list: number[] | undefined, char: number): number[] =>
  (list ?? []).filter((c) => c !== char);

const withChar = (list: number[] | undefined, char: number): number[] =>
  [...without(list, char), char].sort((a, b) => a - b);

/** Apply a mark, clearing whatever that suspect had on the square before. */
export function applyMark(
  marks: BoardMarks,
  char: number,
  cell: CellIdx,
  mark: CellMark,
): BoardMarks {
  const key = String(cell);
  const placements = [...marks.placements];
  const notes = { ...marks.notes };
  const exclusions = { ...marks.exclusions };

  // A suspect stands in one place at a time, and a square holds one person.
  if (mark === "placed") {
    const sittingThere = placements.findIndex((c) => c === cell);
    if (sittingThere !== -1) placements[sittingThere] = null;
    placements[char] = cell;
  } else if (placements[char] === cell) {
    placements[char] = null;
  }

  const setList = (record: Record<string, number[]>, next: number[]): void => {
    if (next.length === 0) delete record[key];
    else record[key] = next;
  };

  setList(notes, mark === "maybe" ? withChar(notes[key], char) : without(notes[key], char));
  setList(exclusions, mark === "no" ? withChar(exclusions[key], char) : without(exclusions[key], char));

  return { placements, notes, exclusions };
}

/**
 * The next suspect a swipe lands on, `delta` steps away, wrapping at the ends and skipping the
 * victim — there is nothing to mark for the victim, so stopping on them would be a dead stop in
 * the middle of the gesture.
 */
export function nextSuspect(
  selected: number,
  delta: number,
  characters: ReadonlyArray<{ isVictim: boolean }>,
): number {
  const total = characters.length;
  if (total === 0) return selected;

  let candidate = selected;
  for (let hops = 0; hops < total; hops++) {
    candidate = (candidate + delta + total) % total;
    if (!characters[candidate]?.isVictim) return candidate;
  }
  return selected;
}
